import type { TFunction } from '@mc/i18n'
import type { SubjectDetailItem } from '../../../types/subjects'
import type {
  HeroStripColumn,
  HeroStripConfig,
} from '../../../utils/deriveHeroStrip'

interface ApplyArgs {
  details: SubjectDetailItem[]
  heroStrip?: HeroStripConfig
  t: TFunction
}

const resolveColumn = (
  column: HeroStripColumn,
  t: TFunction,
): SubjectDetailItem => ({
  label: t(column.labelKey),
  value: column.value ?? t(column.valueFallbackKey),
})

/**
 * Rewrites a subject's static detail entries for the active enrollment window,
 * keyed on each entry's `kind` (never English labels). `applications` swaps the
 * cohort/start-date slots and reorders them; `nextCohort` relabels them in
 * place; `legacy` returns details unchanged.
 */
export const applyEnrollmentStateDetails = ({
  details,
  heroStrip,
  t,
}: ApplyArgs): SubjectDetailItem[] => {
  const { variant, primaryColumn, secondaryColumn } = heroStrip ?? {}
  if (variant !== 'applications' && variant !== 'nextCohort') return details
  if (!primaryColumn || !secondaryColumn) return details

  const primary = resolveColumn(primaryColumn, t)
  const secondary = resolveColumn(secondaryColumn, t)

  if (variant === 'nextCohort') {
    return details.map((item) => {
      if (item.kind === 'startDate') return primary
      if (item.kind === 'cohort') return secondary
      return item
    })
  }

  // applications: cohort → primary (Applications Due), startDate → secondary
  const replaced = details.map((item) => {
    if (item.kind === 'cohort') return primary
    if (item.kind === 'startDate') return secondary
    return item
  })

  const startIdx = details.findIndex((d) => d.kind === 'startDate')
  const cohortIdx = details.findIndex((d) => d.kind === 'cohort')
  if (startIdx === -1 || cohortIdx === -1) return replaced

  // Flip so Applications Due (cohort slot) renders before Start Date.
  const flipped = [...replaced]
  ;[flipped[startIdx], flipped[cohortIdx]] = [
    flipped[cohortIdx],
    flipped[startIdx],
  ]
  return flipped
}
